import { useMemo, useState } from 'react';
import {
  Card, Chip, DChip, DataTable, EmptyState, Icon, Pane, PaneHead, PhoneCards, PhoneScreen,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  daysTo,
  expiryLabel,
  expiryTone,
  useShelf,
  type ShelfItem,
} from '../stock-manager/lib/useStock';
import { FlagLowModal } from './components/NurseModal';
import { NurseSearch } from './components/NurseSearch';
import { NurseMenu } from './components/NurseMenu';

/**
 * Nurse · Stock — `g_stock`. THE WINDOW, NOT THE CONTROLS.
 *
 * Her read-only view of the same shelf the Stock manager keeps: `GET /stock`
 * asks for `stock:read`, which she holds, while every verb on it asks for
 * `stock:write`, which she does not. So this is one shelf with two views rather
 * than two lists that can disagree.
 *
 * EXPIRY IN DAYS, NOT IN DATES. The manager's shelf prints the date on the box
 * because they are ordering against it; she is about to open one, and her
 * question is how long she has got — so the column reads "12 d". Same figure,
 * her units.
 *
 * WHAT SHE CAN DO IS FLAG IT. Flagging reaches the clinic's managers as a
 * notification — a line, not a count — which is the one write a view-only
 * screen is allowed.
 */
/* One stable empty array — `shelf ?? []` is a new array each render, which
   silently defeats every useMemo below it. */
const NONE: ShelfItem[] = [];

export function NurseStockPage() {
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const [searching, setSearching] = useState(false);
  const [flag, setFlag] = useState<string | null>(null);
  const [pickedId, setPicked] = useState<string | null>(null);
  usePageMeta({ title: 'Stock' });

  const { data: shelf, isLoading, isError } = useShelf();
  const items = shelf ?? NONE;

  /* EXPIRY FIRST — her board's own words, so the sort is purely by date rather
     than by the manager's decision ranking. Something undated sorts last. */
  const rows = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          (a.nextExpiry ?? '9999-12-31').localeCompare(b.nextExpiry ?? '9999-12-31') ||
          a.name.localeCompare(b.name),
      ),
    [items],
  );

  const counts = {
    expiring: items.filter((i) => i.expiringSoon).length,
    low: items.filter((i) => i.low).length,
  };
  const picked = items.find((i) => i.id === pickedId) ?? null;
  const [phoneQuery, setPhoneQuery] = useState('');


  const tableRows: TableRow[] = rows.map((item) => {
    const days = daysTo(item.nextExpiry);
    const tone = expiryTone(item.nextExpiry);
    const first = item.lots.find((l) => l.quantity > 0);
    return {
      id: item.id,
      cells: {
        item: (
          <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
            <span className="nr-thumb" aria-hidden="true">
              {item.photoUrl ? <img src={item.photoUrl} alt="" /> : <Icon name="photo" />}
            </span>
            <p className="t-strong">{item.name}</p>
          </div>
        ),
        stock: (
          <span className={`t-body w6 num${item.low ? ' amb' : ''}`}>{item.inStock}</span>
        ),
        expiry: !item.batchTracked ? (
          <span className="t-sec mut">not tracked</span>
        ) : days === null ? (
          <span className="t-sec mut">—</span>
        ) : tone ? (
          <Chip kind={tone} small>{days <= 0 ? 'expired' : `${days} d`}</Chip>
        ) : (
          <span className="t-sec mut">{expiryLabel(item.nextExpiry)}</span>
        ),
        note: item.expiringSoon && first?.lotCode ? (
          <span className="t-sec red">batch {first.lotCode} · use this batch first</span>
        ) : item.expiringSoon ? (
          <span className="t-sec red">use the shortest-dated box first</span>
        ) : item.low ? (
          <span className="t-sec amb">
            low{item.minLevel != null ? ` — below its minimum of ${item.minLevel}` : ''}
          </span>
        ) : null,
      },
    };
  });

  /* `g_s1`: what expires soon leads, red-edged with the days left; everything
     else follows as a name and a figure. */
  const phoneRows: TableRow[] = (() => {
    const q = phoneQuery.trim().toLowerCase();
    const shown = rows.filter((i) => !q || `${i.name} ${i.barcode ?? ''}`.toLowerCase().includes(q));
    const soon = shown.filter((i) => i.expiringSoon);
    const rest = shown.filter((i) => !i.expiringSoon);
    const out: TableRow[] = [];
    if (soon.length) out.push({ id: 'h-soon', section: `Expiring soon · ${soon.length}`, cells: {} });
    for (const item of soon) {
      const first = item.lots.find((l) => l.quantity > 0);
      const days = daysTo(item.nextExpiry);
      out.push({
        id: item.id,
        cells: {
          item: (
            <div style={{ minWidth: 0 }}>
              <p className="t-strong">{item.name}</p>
              <p className="t-cap mut">
                {first?.lotCode ? `batch ${first.lotCode} · use this batch first` : 'use the shortest-dated box first'}
              </p>
            </div>
          ),
          state: days === null ? null : <Chip kind="danger" small>{days <= 0 ? 'expired' : `${days} d`}</Chip>,
        },
      });
    }
    if (rest.length) out.push({ id: 'h-all', section: 'All items', cells: {} });
    for (const item of rest) {
      out.push({
        id: item.id,
        cells: {
          item: (
            <div className="flex" style={{ gap: 10, alignItems: 'center' }}>
              <span className="nr-thumb" aria-hidden="true">
                {item.photoUrl ? <img src={item.photoUrl} alt="" /> : <Icon name="photo" />}
              </span>
              <div style={{ minWidth: 0 }}>
                <p className="t-strong">{item.name}</p>
                <p className={`t-cap ${item.low ? 'amb w6' : 'mut'}`}>
                  {item.inStock} {item.unit || 'pcs'}{item.low ? ' — low' : ''}
                </p>
              </div>
            </div>
          ),
        },
      });
    }
    return out;
  })();

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      paneMenu: (close) => <NurseMenu door="stock" onClose={close} />,
      title: `Stock · ${branchName}`,
      sub: 'view only — the Stock manager adjusts',
      search: 'Search the shelf…',
      onSearch: () => setSearching(true),
      searchSize: 'narrow',
      primary: { label: '⚠ Flag low', onClick: () => setFlag(picked?.name ?? '') },
      /* `maie-phone-nurse/g_s1` — "the window, not the controls". The same
         read-only shelf; the nurse's one verb (flag a low item) rides on the
         card she is looking at rather than a top-bar primary she cannot see
         which row applies to. */
      phone: (
        <PhoneScreen
          className="nurse-phone"
          title={`Stock · ${branchName}`}
          trailing={
            <Chip kind="outline" small className="chip-act w6" onClick={() => setFlag('')}>⚠ Flag low</Chip>
          }
          menu={(close) => <NurseMenu door="stock" onClose={close} />}
          search={{ placeholder: 'Search the shelf', value: phoneQuery, onChange: setPhoneQuery }}
        >
          <PhoneCards
            columns={[
              { key: 'item', label: 'Item', width: 80 },
              { key: 'state', label: 'Expiry', width: 20 },
            ]}
            rows={phoneRows}
            body="item"
            trailing="state"
            cardStyle={(r) => (items.find((i) => i.id === r.id)?.expiringSoon ? { borderColor: 'var(--danger-600)' } : undefined)}
            onOpen={(r) => setFlag(rows.find((i) => i.id === r.id)?.name ?? '')}
            empty={<EmptyState title={isLoading ? 'Reading the shelf…' : 'The shelf is empty'}
              sub={isLoading ? '' : 'Nothing has been received yet. The Stock manager puts it here.'} />}
          />
          <p className="t-sec mut" style={{ padding: '10px var(--m) 0', textAlign: 'center' }}>
            No receive, count, or edit here — the Stock manager holds those
          </p>
        </PhoneScreen>
      ),
      pane: picked ? (
        <ShelfItemPane item={picked} branchName={branchName} onFlag={() => setFlag(picked.name)} />
      ) : (
        <Pane
          head={
            <PaneHead
              overline="Shelf item"
              title="Nothing picked"
              sub={`${branchName} · pick a row to see which batch to open`}
            />
          }
          foot={
            <button
              type="button"
              className="btn btn-outline grow"
              style={{ minHeight: 44 }}
              onClick={() => setFlag('')}
            >
              ⚠ Flag low
            </button>
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              Pick a shelf item — its batches, the one to open first, and what moves it dock here.
            </p>
          </div>
          <Card className="mt12">
            <p className="t-sec sec">Receive · Count · Edit belong to the Stock manager.</p>
          </Card>
        </Pane>
      ),
    },
    [branchName, picked, searching, pickedId, rows, isLoading, phoneQuery],
  );

  return (
    <div className="maie desk nurse-root">
      <div className="dcontent scroll">
        <div className="flex mb12" style={{ gap: 10 }}>
          <p className="t-ovl mut">Expiry first</p>
          <p className="t-sec mut grow">
            {isError
              ? 'the shelf did not come back — nothing has been changed'
              : `the shortest-dated batch is the one to open — ${counts.expiring} expiring · ${counts.low} low`}
          </p>
        </div>

        <DataTable
          caption={`The ${branchName} shelf, expiry first`}
          columns={[
            { key: 'item', label: 'Item', width: 46 },
            { key: 'stock', label: 'In stock', width: 14 },
            { key: 'expiry', label: 'Expiry', width: 18 },
            { key: 'note', label: 'Note', width: 22 },
          ]}
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading the shelf…</p>
              ) : (
                <>
                  <p className="es-t">Nothing is on the shelf yet</p>
                  <p className="t-sec mut">
                    The Stock manager fills it as the clinic receives what it buys. Anything you
                    need that is not here goes as a line — flag it and the managers see it now.
                  </p>
                  <p className="mt12">
                    <DChip kind="warn" onClick={() => setFlag('')}>⚠ Flag low</DChip>
                  </p>
                </>
              )}
            </div>
          }
        />

        <div className="card card-ghost mt16">
          <div className="flex" style={{ gap: 12, alignItems: 'center' }}>
            <p className="t-sec sec grow">
              Untracked things — printer paper, coffee — are not on the shelf list. They go as a
              line, not a count.
            </p>
            <DChip kind="warn" onClick={() => setFlag('')}>⚠ Flag low</DChip>
          </div>
        </div>
        <p className="t-sec mut mt12">
          View only — no receive, count or edit: tasks decrement the shelf, the holder adjusts it.
        </p>
      </div>
      <FlagLowModal open={flag !== null} initial={flag ?? ''} onClose={() => setFlag(null)} />
      <NurseSearch open={searching} onClose={() => setSearching(false)} />
    </div>
  );
}

/** The docked item: which box to open, what it looks like, and what moves it. */
function ShelfItemPane({
  item,
  branchName,
  onFlag,
}: {
  item: ShelfItem;
  branchName: string;
  onFlag: () => void;
}) {
  const first = item.lots.find((l) => l.quantity > 0);
  const days = daysTo(first?.expiresOn ?? null);

  return (
    <Pane
      head={
        <PaneHead
          overline="Shelf item"
          title={item.name}
          sub={`${branchName} · ${item.inStock} in stock`}
          wrapTitle
        />
      }
      foot={
        <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }} onClick={onFlag}>
          ⚠ Flag low
        </button>
      }
    >
      {item.expiringSoon && first ? (
        <Card style={{ borderColor: 'var(--danger-600)' }}>
          <p className="t-sec red w6">
            {first.lotCode ? `Batch ${first.lotCode} · ` : ''}
            {days !== null && days <= 0
              ? 'already expired — do not use it'
              : `expires in ${days} d — use this batch first`}
          </p>
          <p className="t-cap mut mt4">FEFO: the shortest-dated batch is the one to open</p>
        </Card>
      ) : null}

      {item.photoUrl ? (
        <Card>
          <div className="flex" style={{ gap: 12, alignItems: 'center' }}>
            <span className="nr-thumb nr-thumb-lg" aria-hidden="true">
              <img src={item.photoUrl} alt="" />
            </span>
            <p className="t-sec sec grow">
              How the box looks on the shelf — so the right one is picked without reading the label
              twice.
            </p>
          </div>
        </Card>
      ) : null}

      {item.batchTracked && item.lots.some((l) => l.quantity > 0) ? (
        <>
          <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Batches — earliest first</p>
          {item.lots
            .filter((l) => l.quantity > 0)
            .map((l) => (
              <Card key={l.id}>
                <div className="flex">
                  <p className="t-strong grow">
                    {l.lotCode ? `${l.lotCode} · ` : ''}
                    {l.expiresOn ? `exp ${expiryLabel(l.expiresOn)}` : 'no expiry'}
                  </p>
                  <p className="t-strong">{l.quantity}</p>
                </div>
              </Card>
            ))}
        </>
      ) : null}

      <Card>
        <p className="t-ovl mut">What moves it</p>
        <p className="t-sec sec mt4">
          {item.perMonth
            ? `Tasks decrement it as it is given — about ${item.perMonth} a month across the clinic.`
            : 'Tasks decrement it as it is given. Nothing has been dispensed yet.'}
        </p>
      </Card>
      <Card>
        <p className="t-sec sec">Receive · Count · Edit belong to the Stock manager.</p>
      </Card>
    </Pane>
  );
}
